import { httpBatchLink, splitLink } from "@trpc/client";
import superjson from "superjson";
import { trpc } from "./trpc";
import { queryClient } from "./queryClient";
import { reportError } from "./errorReporter";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

async function authHeaders() {
  const requestId = crypto.randomUUID();
  try {
    const clerk = window.Clerk;
    if (!clerk?.session) {
      return { "x-request-id": requestId };
    }
    const token = await clerk.session.getToken();
    return {
      authorization: token ? `Bearer ${token}` : "",
      "x-request-id": requestId,
    };
  } catch (error) {
    reportError(error, "Failed to get Clerk session token", { source: "trpc" });
    return { "x-request-id": requestId };
  }
}

/**
 * tRPC Client with Clerk Authentication.
 *
 * We split the wire into four parallel batches because `httpBatchLink` only
 * resolves a batch when its SLOWEST query finishes — so a slow endpoint bundled
 * with fast ones stalls the whole dashboard:
 *   1) `mirakl.*` — external API (ChannelAdvisor/Mirakl) that rate-limits with
 *      60s Retry-After headers. Rides its own batch so Snowflake queries resolve
 *      independently.
 *   2) `portal.categoryIntelligence` — a synchronous Claude call (~15-50s). On
 *      the Overview tab it is fired alongside sales/returns/inventory/stockflow;
 *      in the default batch its latency would block every other tab's data from
 *      returning. It rides its own batch so the rest of the dashboard is never
 *      gated on the AI response — the Category Intelligence section simply
 *      skeletons until its standalone request completes.
 *   3) `brandReport.get` — the Quarter/All-time report's Claude narrative call.
 *      The first request of a day (per brand/window) synchronously generates +
 *      persists the narrative (~15-50s, same profile as categoryIntelligence);
 *      subsequent requests hit the cached row and return fast. Either way it
 *      must never share a batch with `/home`'s briefing query.
 *   4) `catalog.runMetafieldHarvest` — a synchronous one-shot Claude call
 *      (seconds-scale) that proposes Shopify metafield → canonical field
 *      mappings (Phase 3 PR 3, plan decision 5). Rides its own batch so it
 *      never stalls the `/catalog/metafields` page's other queries/mutations
 *      or anything else sharing the default batch window.
 *   5) everything else — Snowflake-backed portal/admin/etc endpoints.
 */
const miraklLink = httpBatchLink({
  transformer: superjson,
  url: `${API_URL}/trpc`,
  headers: authHeaders,
});

const aiInsightsLink = httpBatchLink({
  transformer: superjson,
  url: `${API_URL}/trpc`,
  headers: authHeaders,
});

const brandReportLink = httpBatchLink({
  transformer: superjson,
  url: `${API_URL}/trpc`,
  headers: authHeaders,
});

const metafieldHarvestLink = httpBatchLink({
  transformer: superjson,
  url: `${API_URL}/trpc`,
  headers: authHeaders,
});

/**
 * Queries go out as GET, so a batch of N concurrent calls becomes ONE URL.
 * Node answers 431 on the preflight and 503 on the request once the URL
 * passes its 16 KB header ceiling (measured 2026-07-23 on `portal.productImages`,
 * again 2026-09-01 on the shipment page's 266 `catalog.getFamilyDetail`
 * calls, which never resolved). `maxURLLength` makes the link split a batch
 * into several requests before it reaches that ceiling; 8 KB leaves room for
 * the bearer token and the rest of the headers.
 */
const MAX_BATCH_URL_LENGTH = 8192;

const defaultLink = httpBatchLink({
  transformer: superjson,
  url: `${API_URL}/trpc`,
  headers: authHeaders,
  maxURLLength: MAX_BATCH_URL_LENGTH,
});

// Studio's writing/review calls can take much longer than page-data queries.
// Keep its batches separate; local preview can also route this lane independently.
const studioLink = httpBatchLink({
  transformer: superjson,
  url: `${API_URL}/trpc`,
  headers: authHeaders,
  maxURLLength: MAX_BATCH_URL_LENGTH,
});

export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition: (op) => op.path.startsWith("studio."),
      true: studioLink,
      false: splitLink({
        condition: (op) => op.path === "portal.categoryIntelligence",
        true: aiInsightsLink,
        false: splitLink({
          condition: (op) => op.path === "brandReport.get",
          true: brandReportLink,
          false: splitLink({
            condition: (op) => op.path === "catalog.runMetafieldHarvest",
            true: metafieldHarvestLink,
            false: splitLink({
              condition: (op) => op.path.startsWith("mirakl."),
              true: miraklLink,
              false: defaultLink,
            }),
          }),
        }),
      }),
    }),
  ],
});

export { trpc, queryClient };
